import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'Organization' | 'LocalBusiness' | 'EducationalOrganization' | 'Course' | 'BlogPosting';
  data: Record<string, any>;
}

export function useSchemaOrg({ type, data }: SchemaOrgProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    let schema: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': type,
      ...data,
    };

    // URLの置き換え
    const replaceUrls = (obj: any): any => {
      if (typeof obj === 'string') {
        return obj.replace(/https:\/\/example\.com/g, siteUrl);
      }
      if (Array.isArray(obj)) {
        return obj.map(replaceUrls);
      }
      if (obj && typeof obj === 'object') {
        const newObj: Record<string, any> = {};
        for (const key in obj) {
          newObj[key] = replaceUrls(obj[key]);
        }
        return newObj;
      }
      return obj;
    };

    schema = replaceUrls(schema);

    const scriptId = `schema-org-${type.toLowerCase()}`;
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    script.textContent = JSON.stringify(schema);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data]);
}

export const organizationSchema = {
  name: 'Cooking Studio',
  description: '料理教室クッキングスタジオ - 初心者からプロを目指す方まで、一人ひとりに寄り添った料理教室。基礎から応用まで丁寧に指導します。',
  url: 'https://example.com',
  logo: 'https://example.com/logo.png',
  image: 'https://example.com/og-image.jpg',
  telephone: '+81-3-1234-5678',
  email: 'info@cookingstudio.example.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '渋谷区道玄坂1-2-3',
    addressLocality: '渋谷区',
    addressRegion: '東京都',
    postalCode: '150-0043',
    addressCountry: 'JP',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 35.6595,
    longitude: 139.7004,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '10:00',
      closes: '20:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday', 'Sunday'],
      opens: '09:00',
      closes: '18:00',
    },
  ],
  sameAs: [
    'https://www.facebook.com/cookingstudio',
    'https://www.instagram.com/cookingstudio',
    'https://twitter.com/cookingstudio',
  ],
  priceRange: '¥6,800 - ¥15,800',
};

export const localBusinessSchema = {
  ...organizationSchema,
  '@type': 'LocalBusiness',
  currenciesAccepted: 'JPY',
  paymentAccepted: 'Cash, Credit Card',
  areaServed: {
    '@type': 'City',
    name: '東京',
  },
};

export const educationalOrganizationSchema = {
  ...organizationSchema,
  '@type': 'EducationalOrganization',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: '受講コース',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Course',
          name: '基礎料理コース',
          description: '料理の基本から学べる初心者向けコース。包丁の使い方、調味料の基礎知識、基本的な調理法を丁寧に指導します。',
          provider: {
            '@type': 'Organization',
            name: 'Cooking Studio',
          },
        },
        price: '8800',
        priceCurrency: 'JPY',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Course',
          name: 'プロフェッショナルコース',
          description: 'プロの技術を習得したい方向けの上級コース。レストラン品質の料理技術を学べます。',
          provider: {
            '@type': 'Organization',
            name: 'Cooking Studio',
          },
        },
        price: '15800',
        priceCurrency: 'JPY',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Course',
          name: '世界の料理コース',
          description: 'イタリアン、フレンチ、中華、エスニックなど、世界各国の本格料理を学べます。',
          provider: {
            '@type': 'Organization',
            name: 'Cooking Studio',
          },
        },
        price: '12800',
        priceCurrency: 'JPY',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Course',
          name: '製菓・製パンコース',
          description: 'ケーキ、クッキー、パンなど、お菓子作りとパン作りの基礎から応用まで学べます。',
          provider: {
            '@type': 'Organization',
            name: 'Cooking Studio',
          },
        },
        price: '10800',
        priceCurrency: 'JPY',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Course',
          name: 'キッズ料理教室',
          description: '小学生向けの楽しい料理教室。食育を通じて、食の大切さと料理の楽しさを学びます。',
          provider: {
            '@type': 'Organization',
            name: 'Cooking Studio',
          },
        },
        price: '6800',
        priceCurrency: 'JPY',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Course',
          name: 'オンラインレッスン',
          description: '自宅で学べるオンライン料理教室。ライブ配信で講師と一緒に料理を作ります。',
          provider: {
            '@type': 'Organization',
            name: 'Cooking Studio',
          },
        },
        price: '7800',
        priceCurrency: 'JPY',
      },
    ],
  },
};
